import {
  DashboardTileStatus,
} from "../../services/zeos-api/contracts/Dashboard";

export type UnlockedTileBodyState =
  | "loading"
  | "partial"
  | "metrics"
  | "not_connected"
  | "no_asset"
  | "error";

/** Tile has a feed (including history still backfilling). Used to unlock vs Explore CTA. */
export const dashboardTileHasFeed = (
  status: DashboardTileStatus | undefined
): boolean => status === "ok" || status === "partial" || status === "error";

export const dashboardTileHasUsableMetrics = (
  metrics: Array<{ value: number | null | undefined }>
): boolean =>
  metrics.some(
    (metric) =>
      metric.value != null &&
      !Number.isNaN(metric.value) &&
      metric.value !== 0
  );

export const resolveUnlockedBodyState = (
  status: DashboardTileStatus | undefined,
  loading?: boolean,
  hasUsableMetrics?: boolean
): UnlockedTileBodyState => {
  if (loading) {
    return "loading";
  }
  if (!status) {
    return "error";
  }
  if (status === "not_connected") {
    return "not_connected";
  }
  if (status === "no_asset") {
    return "no_asset";
  }
  if (status === "error") {
    return "error";
  }
  if (status === "partial") {
    return hasUsableMetrics ? "metrics" : "partial";
  }
  return "metrics";
};

export interface IFormatTileMetricOptions {
  /** `$` is prefixed on the number; other units are rendered separately in StatMetric. */
  unit?: string;
  abs?: boolean;
}

/** Prefers the API unit when present so overview tiles cannot invent kWh/t independently. */
export const dashboardUnitOr = (
  apiUnit: string | null | undefined,
  fallback: string
): string => {
  if (apiUnit == null) {
    return fallback;
  }
  const trimmed = apiUnit.trim();
  return trimmed === "" ? fallback : trimmed;
};

/**
 * Formats a server-computed metric value only (`$1,234.56` for currency, otherwise bare
 * number). The server has already rounded the value per the metric dictionary; this only
 * caps display precision so `toLocaleString`'s locale defaults can't re-round it further.
 * The single formatter for all dashboard metric display — legacy tiles and NextGen KPIs.
 */
export const formatMetricNumber = (
  value: number,
  options?: IFormatTileMetricOptions
): string => {
  const numeric = options?.abs ? Math.abs(value) : value;
  if (options?.unit === "$") {
    // Signed dollars read "-$74", not "$-74" (Savings Generated can be negative).
    const dollars = Math.abs(numeric).toLocaleString("en-US", {
      maximumFractionDigits: 4,
    });
    return numeric < 0 ? `-$${dollars}` : `$${dollars}`;
  }
  return numeric.toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
};

/** Tile-status-aware wrapper around `formatMetricNumber` for legacy overview tiles. */
export const formatTileMetric = (
  status: DashboardTileStatus | undefined,
  value: number | null | undefined,
  options?: IFormatTileMetricOptions
): string => {
  if (
    !status ||
    status === "not_connected" ||
    status === "error" ||
    status === "no_asset"
  ) {
    return "—";
  }
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return formatMetricNumber(value, options);
};
